import { type MouseEvent, useCallback, useRef, useState } from 'react';

import cn from 'classnames';
import RcSelect, { Option } from 'rc-select';
import type { RenderDOMFunc } from 'rc-select/lib/interface';
import type { FilterFunc } from 'rc-select/lib/Select';

import { fontClassNames } from 'font';

import { Icon } from 'ui/atoms/Icon/Icon';

import type { SelectProps } from './Select.types';

const sizeClassMap: Record<string, string> = {
  xl: 'gdb-select--xl',
  lg: 'gdb-select--lg',
  md: 'gdb-select--md',
  sm: 'gdb-select--sm',
  64: 'gdb-select--lg',
  48: 'gdb-select--md',
  32: 'gdb-select--sm',
};

const filterOption: FilterFunc<any> = (input, option) => {
  if (!option) {
    return;
  }

  if (option.searchText) {
    return option.searchText.toLowerCase().includes(input.toLowerCase());
  }

  if (typeof option.title === 'string') {
    return option.title.toLowerCase().includes(input.toLowerCase());
  }

  return;
};

export const Select = <T extends unknown>({
  hasStateOptions,
  filterOptions: filterOptionsAsProp,
  dropdownClassName = '',
  className = '',
  label,
  icon,
  options,
  onChange,
  value,
  disabled,
  cancelable,
  placeholder = '',
  hasError,
  fullWidth = false,
  noBorder = false,
  theme = 'light',
  size = 64,
  showSearch,
  ...props
}: SelectProps<T>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const ignoreNextClose = useRef(false);

  const getSelector = () => wrapperRef.current?.querySelector<HTMLElement>('.rc-select-selector') ?? null;
  const getSearchInput = () =>
    wrapperRef.current?.querySelector<HTMLInputElement>('.rc-select-selection-search-input') ?? null;

  const onChangeHandler = (newValue: any) => {
    onChange(newValue as T);
  };

  const onDropdownVisibleChange = useCallback((visible: boolean) => {
    const shouldIgnore = !visible && ignoreNextClose.current;
    ignoreNextClose.current = false;
    if (!shouldIgnore) {
      setOpen(visible);
    }
  }, []);

  const handleTriggerMouseDown = (e: MouseEvent) => {
    if (getSelector()?.contains(e.target as Node)) return; // rc-select handles selector clicks directly
    e.preventDefault(); // prevent focus change on label/icon click
    ignoreNextClose.current = true;
    if (!open) {
      setOpen(true);
      getSearchInput()?.focus();
    }
  };

  const handleArrowMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    ignoreNextClose.current = true;
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      getSearchInput()?.focus();
    }
  };

  const popupContainer: RenderDOMFunc = props.getPopupContainer
    ? (props.getPopupContainer as RenderDOMFunc)
    : () => portalRef.current!;

  const optionsList = props.loading ? (
    <Option className={fontClassNames} value="::none::">
      Loading ...
    </Option>
  ) : (
    options.map((option) => (
      <Option
        className={fontClassNames}
        value={option.value}
        key={option.searchText || String(option.value)}
        title={typeof option.title === 'string' ? option.title : undefined}
        searchText={option.searchText}
      >
        {option.extra ? (
          <div className="gdb-select-extra">
            <span>{option.title}</span>
            <span className="gdb-select-extra-label">{option.extra}</span>
          </div>
        ) : (
          <span>{option.title}</span>
        )}
      </Option>
    ))
  );

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'gdb-select',
        'select',
        sizeClassMap[String(size)],
        disabled && 'gdb-select--disabled',
        hasError && 'gdb-select--error',
        fullWidth && 'gdb-select--full-width',
        noBorder && 'gdb-select--no-border',
        theme === 'dark' && 'gdb-select--dark',
        label && 'gdb-select--with-label',
        className,
      )}
    >
      {label ? (
        <div className="gdb-select-trigger" onMouseDown={handleTriggerMouseDown}>
          {icon && <Icon type={icon} size="small" className="gdb-select-icon" />}
          <div className="gdb-select-field">
            <span className="gdb-select-label">{label}</span>
            <RcSelect
              getPopupContainer={popupContainer}
              showSearch={showSearch ?? false}
              suffixIcon={null}
              dropdownMatchSelectWidth={false}
              optionFilterProp="children"
              filterOption={filterOptionsAsProp || filterOption}
              dropdownClassName={cn('gdb-select-dropdown', dropdownClassName)}
              className={fontClassNames}
              placeholder={placeholder}
              value={value || undefined}
              onChange={onChangeHandler}
              disabled={disabled}
              open={open}
              onDropdownVisibleChange={onDropdownVisibleChange}
              aria-label="Select from list"
            >
              {optionsList}
            </RcSelect>
          </div>
        </div>
      ) : (
        <RcSelect
          getPopupContainer={popupContainer}
          showSearch
          suffixIcon={null}
          dropdownMatchSelectWidth={false}
          optionFilterProp="children"
          filterOption={filterOptionsAsProp || filterOption}
          dropdownClassName={cn('gdb-select-dropdown', dropdownClassName)}
          className={fontClassNames}
          placeholder={placeholder}
          value={value || undefined}
          onChange={onChangeHandler}
          disabled={disabled}
          open={open}
          onDropdownVisibleChange={onDropdownVisibleChange}
          aria-label="Select from list"
        >
          {optionsList}
        </RcSelect>
      )}

      <span className="gdb-select-arrow" aria-hidden="true" onMouseDown={handleArrowMouseDown}>
        <Icon type="arrow-2" />
      </span>

      <div ref={portalRef} className="gdb-select-portal" aria-hidden="true" />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default Select;
